// App Dashboard Page - Displays CAB details for application
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Paper,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Science as ScienceIcon,
  Badge as BadgeIcon,
  Timeline as TimelineIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  Person2Outlined,
  VisibilityOutlined as ScrutinyIcon,
  History as HistoryIcon,
  Person,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import axios from 'axios';
import { getApplicationStage } from '../../../services/api';
import DoApiService from "../../../services/DoApiService";
import { decryptAppId, decryptText, encryptAppId, encryptText } from "../../../utils/encryption";
import ReportsDetails from "../../Do/application/ReportsDetails";
import PaymentDetails from "../../Do/application/PaymentDetails";
import EmailNotificationDetails from "../../Do/application/EmailNotificationDetails";
import AssessorDetailsViewJD from "./AssessorDetailsViewJD";
import acProcessService from '../../../services/acProcessService';
import { generateCommunicationLetter } from "../../../components/certificate/CommunicationLetterGenerator";
import { generateDecisionLetter } from "../../../components/certificate/DecisionLetterGenerator";
import { toast } from "react-toastify";
import CommonAPIService from "../../../services/commonAPIService";


const AppDashboard = () => {
  // State management
  const [cabDetails, setCabDetails] = useState(null);
  const [dealingOfficer, setDealingOfficer] = useState(null);
  const [contactPersons, setContactPersons] = useState([]); // Changed to array for multiple contacts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openOfficerDialog, setOpenOfficerDialog] = useState(false);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [rejectedRemarks, setRejectedRemarks] = useState([]);
  const [closeRemarks, setCloseRemarks] = useState([]);
  // Dialog open/close handlers
  const handleOpenOfficerDialog = () => setOpenOfficerDialog(true);
  const handleCloseOfficerDialog = () => setOpenOfficerDialog(false);
  const handleOpenContactDialog = () => setOpenContactDialog(true);
  const handleCloseContactDialog = () => setOpenContactDialog(false);

  // Certificate Approval states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [disapproveDialogOpen, setDisapproveDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [approvePassword, setApprovePassword] = useState('');
  const [disapprovePassword, setDisapprovePassword] = useState('');
  const [disapproveRemarks, setDisapproveRemarks] = useState('');
  const [ceoList, setCeoList] = useState([]);
  const [loadingCeoList, setLoadingCeoList] = useState(false);
  const [selectedCeo, setSelectedCeo] = useState('');
  const [success, setSuccess] = useState(null);
  const [applicationData, setApplicationData] = useState(null);


  // Router hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedLabTypeId = searchParams.get("labTypeId");

  // Decrypt parameters
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const labTypeId = encryptedLabTypeId ? decryptText(encryptedLabTypeId) : null;
  const [applicationStage, setApplicationStage] = useState(null);
   const [form11Status, setForm11Status] = useState({ hasEntry: false, isAccept: null, acceptReason: null });
  

  // Load dealing officer details (mode 31)
  const loadDealingOfficer = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAllotmentDetails({
        mode: 31,
        appId: parseFloat(appId),
      });

      if (response.success && response.data.length > 0) {
        setDealingOfficer(response.data[0]); // Take first officer
      }
    } catch (err) {
      console.error("Load dealing officer error:", err);
    }
  }, [appId]);

  const LoadApplicationStage = useCallback(async () => {
    if (!appId) return;
    try {
      const stageResponse = await getApplicationStage(appId);
      if (stageResponse.data.success) {
        setApplicationStage(stageResponse.data.stage);
      }
    } catch (err) {
      console.error("Load application stage error:", err);
    }
  }, [appId]);

  // Load contact person details (mode 27)
  const loadContactPerson = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAllotmentDetails({
        mode: 27,
        appId: parseFloat(appId),
      });

      if (response.success && response.data.length > 0) {
        setContactPersons(response.data); // Store all contact persons (lab head + CEO details)
      }
    } catch (err) {
      console.error("Load contact person error:", err);
    }
  }, [appId]);

  // Load rejected remarks
  const loadRejectedRemarks = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getRejectedRemarks({
        appId: parseFloat(appId),
      });

      if (response.success && response.data.length > 0) {
        setRejectedRemarks(response.data);
      }
    } catch (err) {
      console.error("Load rejected remarks error:", err);
    }
  }, [appId]);


  // Load close remarks
  const loadCloseRemarks = useCallback(async () => {
    if (!appId) return;
    try {
      const response = await DoApiService.getCloseRemarks({ appId: parseFloat(appId) });
      if (response.success && response.data.length > 0) {
        setCloseRemarks(response.data);
      }
    } catch (err) {
      console.error("Load close remarks error:", err);
    }
  }, [appId]);

  // Load CAB details on page load (equivalent to .NET getCABDetailAll)
  const loadCABDetails = useCallback(async () => {
    if (!appId) {
      setError("Invalid application ID. Please check the URL.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call API to get CAB details (mode 100 equivalent)
      const response = await DoApiService.getCABDetails({
        appId: parseFloat(appId), // Convert to double as per .NET code
      });

      if (response.success) {
        setCabDetails(response.data);

        // Store in session equivalent (localStorage)
        localStorage.setItem("AppDashboard_AppId", appId);
        localStorage.setItem("AppDashboard_OrgId", orgId || "");
        localStorage.setItem("AppDashboard_LabTypeId", labTypeId || "");

        // Load additional details
        await Promise.all([loadDealingOfficer(), loadContactPerson(), loadRejectedRemarks(), loadCloseRemarks()]);
      } else {
        throw new Error(response.message || "Failed to load CAB details");
      }
    } catch (err) {
      setError(`Error loading CAB details: ${err.message}`);
      console.error("Load CAB details error:", err);
    } finally {
      setLoading(false);
    }
  }, [appId, orgId, labTypeId, loadDealingOfficer, loadContactPerson]);

  // Load data when component mounts
  useEffect(() => {
    loadCABDetails();
    LoadApplicationStage();
  }, [loadCABDetails, LoadApplicationStage, loadRejectedRemarks, loadCloseRemarks]);

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  // Fetch CEO/Director list for approval
  const fetchCeoList = async (appId) => {
    try {
      setLoadingCeoList(true);
      const response = await axios.get(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/ceolist`, {
        params: {
          appid: appId,
          usertypeid: 8,
        }
      });

      if (response.data && response.data.ceo) {
        setCeoList(response.data.ceo);
      } else {
        setCeoList([]);
      }
    } catch (error) {
      console.error('Error fetching CEO list:', error);
      setError('Error loading CEO list');
      setCeoList([]);
    } finally {
      setLoadingCeoList(false);
    }
  };

  // Handle Approve Certificate
  const handleApproveCertificate = () => {
    setSelectedApplication(cabDetails);
    setApprovePassword('');
    setSelectedCeo('');
    fetchCeoList(appId);
    setApproveDialogOpen(true);
  };

  // Confirm Approve Certificate
  const confirmApproveCertificate = async () => {
    if (!selectedApplication) return;

    if (!approvePassword || approvePassword.trim() === '') {
      setError('Please enter your password');
      return;
    }

    if (!selectedCeo || selectedCeo.trim() === '') {
      setError('Please select a CEO');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Verify password first
      const orgIdFromStorage = localStorage.getItem('orgId');
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
        {
          orgId: orgIdFromStorage,
          password: approvePassword,
        }
      );

      if (!passwordResponse.data.success) {
        setError('Invalid password. Please try again.');
        setLoading(false);
        return;
      }

      // Approve certificate using directorapprove API
      const payload = {
        forwaredTo: selectedCeo,
        forwardBy: orgIdFromStorage,
        forwardReason: 'Certificate Approved',
        appId: appId.toString(),
        forwardType: 'Certificate',
        forwardByrole: '9', // JD role
        forwardToRole: '8' // Director role
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/directorapprove`,
        payload
      );

      if (response.data.success || response.data.isstatus) {
        setSuccess('Certificate approved successfully!');
        await acProcessService.updateApplicationStage(appId, '7b');
        setApproveDialogOpen(false);
        setSelectedApplication(null);
        setApprovePassword('');
        setSelectedCeo('');
        // Reload page to refresh data
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to approve certificate');
      }
    } catch (err) {
      console.error('Approve certificate error:', err);
      setError(err.response?.data?.message || 'Failed to approve certificate');
    } finally {
      setLoading(false);
    }
  };

  // Handle Disapprove Certificate
  const handleDisapproveCertificate = () => {
    setSelectedApplication(cabDetails);
    setDisapprovePassword('');
    setDisapproveRemarks('');
    setDisapproveDialogOpen(true);
  };

  // Confirm Disapprove Certificate
  const confirmDisapproveCertificate = async () => {
    if (!selectedApplication) return;

    if (!disapprovePassword || disapprovePassword.trim() === '') {
      setError('Please enter your password');
      return;
    }

    if (!disapproveRemarks || disapproveRemarks.trim() === '') {
      setError('Please provide remarks for disapproval');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Verify password first
      const orgIdFromStorage = localStorage.getItem('orgId');
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
        {
          orgId: orgIdFromStorage,
          password: disapprovePassword,
        }
      );

      if (!passwordResponse.data.success) {
        setError('Invalid password. Please try again.');
        setLoading(false);
        return;
      }

      // Disapprove certificate using directorapprove API
      const payload = {
        forwaredTo: '',
        forwardBy: orgIdFromStorage,
        forwardReason: disapproveRemarks.trim(),
        appId: appId.toString(),
        forwardType: 'Certificate',
        forwardByrole: '9', // JD role
        forwardToRole: '8' // Director role for disapproval
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/directorapprove`,
        payload
      );

      if (response.data.success || response.data.isstatus) {
        setSuccess('Certificate disapproved successfully!');
        setDisapproveDialogOpen(false);
        setSelectedApplication(null);
        setDisapprovePassword('');
        setDisapproveRemarks('');
        // Reload page to refresh data
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to disapprove certificate');
      }
    } catch (err) {
      console.error('Disapprove certificate error:', err);
      setError(err.response?.data?.message || 'Failed to disapprove certificate');
    } finally {
      setLoading(false);
    }
  };
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Handle View Communication Letter
  const handleViewCommunicationLetter = async () => {

    try {
      toast.info("Fetching communication letter...");

      // Load application details if not already loaded
      let appDetails = applicationData;
      if (!appDetails) {
        appDetails = await loadApplicationDetails(appId);
      }

      if (!appDetails) {
        toast.warning("Application data not loaded. Please try again.");
        return;
      }

      // Load communication data and get it directly (don't rely on state)
      const comminicationData = await loadCommunicationData(
        appId,
        orgId,
      );

      const letterData = {
        applicationNo: appDetails.ApplicationNo || "N/A",
        letterDate: comminicationData.date ? new Date(comminicationData.date).toLocaleDateString("en-GB") : '',
        contactPerson: appDetails.ContactPerson || "N/A",
        laboratoryName: appDetails.LaboratoryName || "N/A",
        address: appDetails.Address || "N/A",
        extAddress: appDetails.ExtAddress || "N/A",
        mobile: appDetails.ContactMobile || "N/A",
        email: appDetails.ContactEmail || "N/A",
        subject: comminicationData.subject || "N/A",
        body: comminicationData.body || "N/A",
        advisoryList: [],
        doName: appDetails.DoName || "N/A",
        doEmail: appDetails.DoEmail || "N/A",
        showAdverseNote: false,
        showNewAddress: new Date() > new Date("2025-07-18"),
      };

      await generateCommunicationLetter(letterData);
      toast.success("Communication letter downloaded successfully");
    } catch (err) {
      console.error("Error generating letter:", err);
      toast.error("Failed to generate communication letter");
    }
  };

  // Handle View Decision Letter
  const handleViewDecisionLetter = async () => {

    try {
      toast.info("Fetching decision letter...");

      // Load application details if not already loaded
      let appDetails = applicationData;
      if (!appDetails) {
        appDetails = await loadApplicationDetails(appId);
      }

      if (!appDetails) {
        toast.warning("Application data not loaded. Please try again.");
        return;
      }

      // Load decision data and get it directly (don't rely on state)
      const decisionData = await loadDecisionData(appId, orgId);

      const letterData = {
        applicationNo: appDetails.ApplicationNo || "N/A",
        letterDate: decisionData.date ? new Date(decisionData.date).toLocaleDateString("en-GB") : '',
        contactPerson: appDetails.ContactPerson || "N/A",
        laboratoryName: appDetails.LaboratoryName || "N/A",
        address: appDetails.Address || "N/A",
        extAddress: appDetails.ExtAddress || "N/A",
        mobile: appDetails.ContactMobile || "N/A",
        email: appDetails.ContactEmail || "N/A",
        subject: decisionData.subject || "N/A",
        body: decisionData.body || "N/A",
        advisoryList: [],
        doName: appDetails.DoName || "N/A",
        doEmail: appDetails.DoEmail || "N/A",
        showAdverseNote: false,
        showNewAddress: new Date() > new Date("2025-07-18"),
      };

      await generateDecisionLetter(letterData);
      toast.success("Decision letter downloaded successfully");
    } catch (err) {
      console.error("Error generating letter:", err);
      toast.error("Failed to generate decision letter");
    }
  };

  const handleDownloadCertificate = () => {
    window.open(`/main/viewCertificate?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&action=${encryptText('download')}`, '_blank');
  };

  // Load application details for communication letter
  const loadApplicationDetails = async (appId) => {
    try {
      const [appDetailsResponse, labHeadResponse, doResponse] =
        await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/ac-process/application/details/${appId}`,
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: { mode: 27, appId: appId },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: { mode: 31, appId: appId },
          }),
        ]);

      const appData = appDetailsResponse.data?.data || {};
      const labHeadData = labHeadResponse.data?.data?.[0] || {};
      const doData = doResponse.data?.data?.[0] || {};

      const appDetails = {
        ApplicationNo: appData.ApplicationNo || "N/A",
        LaboratoryName: appData.LaboratoryName || "N/A",
        ContactPerson: labHeadData.name || "N/A",
        Address: labHeadData.address || "N/A",
        ExtAddress:
          `${labHeadData.city || "N/A"} ${labHeadData.state || "N/A"} ${labHeadData.pincode || "N/A"}`.trim(),
        ContactMobile: labHeadData.mobile || "N/A",
        ContactEmail: labHeadData.email || "N/A",
        DoName: doData.name || "N/A",
        DoEmail: doData.email || "N/A",
        CommunicationLetterSent: appData.CommunicationLetterSent || 0,
      };

      setApplicationData(appDetails);
      return appDetails;
    } catch (err) {
      console.error("Error loading application details:", err);
      return null;
    }
  };
   const handleForm11 = async () => {
      try {
        const response = await CommonAPIService.getAssessmentSendToNABLDate(appId);
        if (response.success && response.data) {
          if (response.data.isDisplayNew) {
            const url = `/main/do/form11-new?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}`;
            navigate(url);
          } else {
            const url = `/main/do/form11?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}`;
            navigate(url);
          }
        } else {
          const url = `/main/do/form11?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}`;
          navigate(url);
        }
      } catch (error) {
        console.error("Form11 navigation error:", error);
        const url = `/main/do/form11?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}`;
        navigate(url);
      }
    };

  // Load decision data
  const loadDecisionData = async (appId, orgId) => {
    try {
      const commResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getdecision`,
        {
          params: { appid: appId, orgid: orgId },
        },
      );

      const subject = commResponse.data?.subject || "";
      const body = commResponse.data?.body || "";
      const date = commResponse.data?.date || "";
      // Return the data for immediate use
      return {
        subject,
        body,
        date
      };
    } catch (err) {
      console.error("Error loading decision data:", err);
      return {
        subject: "",
        body: "",
        date: ""
      };
    }
  };
  
    const loadForm11Status = useCallback(async () => {
      if (!appId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000/reactApi/api"}/do/request-form11-status`,
          { params: { appId } }
        );
        setForm11Status({
          hasEntry: response.data.hasEntry === true,
          isAccept: response.data.isAccept ?? null,
          acceptReason: response.data.acceptReason ?? null,
        });
      } catch (error) {
        console.error("Form11 status error:", error);
      }
    }, [appId]);
     useEffect(() => {
        loadForm11Status();
      }, [loadForm11Status]);
    

  // Load communication data
  const loadCommunicationData = async (appId, orgId) => {
    try {
      const commResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetCommunication`,
        {
          params: { appid: appId, orgid: orgId },
        },
      );

      const subject = commResponse.data?.subject || "";
      const body = commResponse.data?.body || "";
      const date = commResponse.data?.date || "";
      // Return the data for immediate use
      return {
        subject,
        body,
        date
      };
    } catch (err) {
      console.error("Error loading communication data:", err);
      return {
        subject: "",
        body: "",
        date: ""
      };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Dealing Officer Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (!cabDetails) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No CAB details found for this application
        </Typography>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        pt: 2,
        pb: 4,
      }}
    >
      {/* Header with Gradient Background */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackClick}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Applications
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Dashboard
            </Typography>
          </Breadcrumbs>

          {/* Header Content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                <DashboardIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                   {cabDetails.laboratoryName || cabDetails.labName}
                </Typography>

                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {cabDetails.applicationNo} • {cabDetails.labType} •
                  <Chip
                    label={`Standard: ${cabDetails.standard}`}
                    variant="outlined"
                    size="small"
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                    }}
                  />
                  <Chip
                    icon={<Person2Outlined sx={{ color: "white !important" }} />}
                    label="Contact Persons"
                    variant="outlined"
                    size="small"
                    onClick={handleOpenContactDialog}
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderColor: "white",
                      },
                    }}
                  />
                  <Chip
                    icon={<Person2Outlined sx={{ color: "white !important" }} />}
                    label="Dealing Officer"
                    variant="outlined"
                    size="small"
                    onClick={handleOpenOfficerDialog}
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderColor: "white",
                      },
                    }}
                  />
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: 2 }}>
        {/* Key Metrics Cards */}
        <Grid container spacing={3}>
          {/* Current Stage */}
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #fe8f4fff 0%, #fe3b00ff 100%)",
                color: "white",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(79, 172, 254, 0.25)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 40px rgba(79, 172, 254, 0.35)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      width: 48,
                      height: 48,
                    }}
                  >
                    <TimelineIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <ScheduleIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}
                >
                  Current Stage
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {cabDetails.stage || "N/A"} <br />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Last Updated
                  </Typography>{" "}
                  {formatDate(cabDetails.lastUpdated)} <br />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Pending At
                  </Typography>{" "}
                  {cabDetails.PendingAt || "N/A"}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Submission Date
                  </Typography>{" "}
                  {formatDate(cabDetails.submissionDate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "success.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <DescriptionIcon />
                  </Avatar>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Quick Actions
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ScrutinyIcon />}
                      onClick={() => {
                        const params = new URLSearchParams({
                          appId: encryptedAppId,
                          orgId: encryptedOrgId,
                          labTypeId: encryptedLabTypeId
                        });
                        navigate(`/main/jd/application/form8?${params.toString()}`);
                      }}
                    >
                      Form 8
                    </Button>
                     {form11Status.hasEntry && (
                                            <Button
                                              variant="contained"
                                              startIcon={<DescriptionIcon />}
                                              onClick={() => handleForm11()}
                                              sx={{
                                                py: 1.5,
                                                borderRadius: 2,
                                                textTransform: "none",
                                                fontWeight: 600,
                                                backgroundColor: "#2e7d32",
                                                "&:hover": { backgroundColor: "#1b5e20" },
                                              }}
                                            >
                                              Form 11
                                            </Button>
                                          )}
                    {applicationStage && (applicationStage === '20' || applicationStage === '7a') && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ScrutinyIcon />}
                        onClick={() => {
                          const params = new URLSearchParams({
                            appId: encryptedAppId,
                            orgId: encryptedOrgId,
                            labTypeId: encryptedLabTypeId
                          });
                          navigate(`/main/jd/application/form9?${params.toString()}`);
                        }}
                      >
                        Form 9
                      </Button>
                    )}
                    {applicationStage && applicationStage === '7a' && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={handleApproveCertificate}
                        >
                          Forward to Director
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={handleDisapproveCertificate}
                        >
                          Request Changes on Certificate
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewCommunicationLetter}
                        >
                          View Communication Letter
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewDecisionLetter}
                        >
                          View Decision Letter
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadCertificate}
                        >
                          Download Certificate
                        </Button>
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Close Remarks Card */}
          {closeRemarks.length > 0 && (
            <Grid item xs={12}>
              <Card
                sx={{
                  background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
                  color: "white",
                  borderRadius: 3,
                  boxShadow: "0 8px 32px rgba(239, 68, 68, 0.25)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(239, 68, 68, 0.35)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48, mr: 2 }}>
                      <CloseIcon sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
                      Close Remarks
                    </Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 700, fontSize: 14 }}>
                            Remark
                          </TableCell>
                          <TableCell sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 700, fontSize: 14 }}>
                            File Upload
                          </TableCell>
                          <TableCell sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 700, fontSize: 14 }}>
                            Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {closeRemarks.map((remark, index) => (
                          <TableRow key={index} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}>
                            <TableCell sx={{ color: "white", fontSize: 13 }}>
                              {remark.Remark || "N/A"}
                            </TableCell>
                            <TableCell sx={{ color: "white", fontSize: 13 }}>
                              {remark.Fileupload ? (
                                <a
                                  href={`https://nabl7t.s3.ap-south-1.amazonaws.com/live/${remark.Fileupload}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "white", textDecoration: "underline" }}
                                >
                                  {remark.Fileupload}
                                </a>
                              ) : "N/A"}
                            </TableCell>
                            <TableCell sx={{ color: "white", fontSize: 13 }}>
                              {formatDate(remark.Insertdate || remark.InsertDate || "")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Rejected Remarks Card */}
          {rejectedRemarks.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "white",
                  borderRadius: 3,
                  boxShadow: "0 8px 32px rgba(245, 158, 11, 0.25)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(245, 158, 11, 0.35)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        width: 48,
                        height: 48,
                        mr: 2,
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, fontSize: 18 }}
                    >
                      Rejection Remarks
                    </Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              bgcolor: "rgba(255,255,255,0.2)",
                              color: "white",
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            Rejected By
                          </TableCell>
                          <TableCell
                            sx={{
                              bgcolor: "rgba(255,255,255,0.2)",
                              color: "white",
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            Reason
                          </TableCell>
                          <TableCell
                            sx={{
                              bgcolor: "rgba(255,255,255,0.2)",
                              color: "white",
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rejectedRemarks.map((remark, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                bgcolor: "rgba(255,255,255,0.1)",
                              },
                            }}
                          >
                            <TableCell sx={{ color: "white", fontSize: 13 }}>
                              {remark.RemarkByName || "N/A"}
                            </TableCell>
                            <TableCell sx={{ color: "white", fontSize: 13 }}>
                              {remark.Remark || "N/A"}
                            </TableCell>
                            <TableCell sx={{ color: "white", fontSize: 13 }}>
                              {formatDate(remark.InsertDate || "")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

        </Grid>



        {/* Main Content Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Dealing Officer Details Button */}
          <Grid item xs={12} lg={4}>
            {/* Dialog for Dealing Officer Information */}
            <Dialog
              open={openOfficerDialog}
              onClose={handleCloseOfficerDialog}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Dealing Officer Information</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <BadgeIcon />
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Dealing Officer
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Officer Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.name || "Not assigned"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.email || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Contact
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.mobile ||
                          dealingOfficer?.email ||
                          "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseOfficerDialog}
                  color="primary"
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>

          {/* Contact Person Details Button */}
          <Grid item xs={12} lg={4}>
            {/* Dialog for Contact Person Information */}
            <Dialog
              open={openContactDialog}
              onClose={handleCloseContactDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Contact Person Information</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "secondary.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Laboratory Contacts
                  </Typography>
                </Box>

                {contactPersons.length > 0 ? (
                  <Grid container spacing={3}>
                    {contactPersons.map((contact, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: '100%',
                            border: contact.isPrimary ? '2px solid #1976d2' : '1px solid #e0e0e0'
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar
                                sx={{
                                  bgcolor: contact.recordType === 'lab_head_contact' ? 'primary.main' : 'success.main',
                                  mr: 2,
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                <Person2Outlined sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {contact.recordType === 'lab_head_contact' ? 'Contact Person Details' : 'CEO Details'}
                                </Typography>
                                {/* {contact.isPrimary && (
                                  <Chip 
                                    label="Primary" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ mt: 0.5 }} 
                                  />
                                )} */}
                              </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                Name
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, mt: 0.5 }}
                              >
                                {contact.name || "Not available"}
                              </Typography>
                            </Box>


                            {contact.email && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Email
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mt: 0.5 }}
                                >
                                  {contact.email}
                                </Typography>
                              </Box>
                            )}

                            {contact.mobile && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Mobile
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mt: 0.5 }}
                                >
                                  {contact.mobile}
                                </Typography>
                              </Box>
                            )}

                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No contact information available
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseContactDialog}
                  color="secondary"
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>

        {/* Assessor Details Section */}
        <Grid item xs={12} >
          <AssessorDetailsViewJD appId={encryptedAppId} orgId={encryptedOrgId} type="JD" stage={cabDetails.CurrentStage} labTypeId={cabDetails.labTypeId} />
        </Grid>

        {/* Reports Details Section */}
        <ReportsDetails appId={appId} orgId={orgId} />

        {/* Payment Details Section */}
        <PaymentDetails appId={appId} />

        {/* Email Notification Details Section */}
        <EmailNotificationDetails appId={appId} isCab={false} />
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ position: 'fixed', top: 80, right: 20, zIndex: 9999 }}>
          {success}
        </Alert>
      )}

      {/* Approve Certificate Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !loading && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="success" />
            Forward to Director
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to forward the certificate for this application?
          </Typography>
          {cabDetails && (
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2">
                Application No: {cabDetails.applicationNo}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Status: {cabDetails.stage}
              </Typography>
            </Box>
          )}

          {/* Select Director from List */}
          {loadingCeoList && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loadingCeoList && ceoList.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Director</InputLabel>
              <Select
                value={selectedCeo}
                onChange={(e) => setSelectedCeo(e.target.value)}
                label="Select Director"
                disabled={loading}
              >
                <MenuItem value="">--Select Director--</MenuItem>
                {ceoList.map((ceo, index) => (
                  <MenuItem
                    key={index}
                    value={ceo.OrgId || ceo.orgId || ceo.Id || ceo.id}
                  >
                    {ceo.Name || ceo.name || 'N/A'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            type="password"
            label="Password *"
            placeholder="Enter your password..."
            value={approvePassword}
            onChange={(e) => setApprovePassword(e.target.value)}
            disabled={loading}
            error={!approvePassword && error}
            helperText={!approvePassword && error ? 'Password is required' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveDialogOpen(false);
              setApprovePassword('');
              setSelectedCeo('');
              setSelectedApplication(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmApproveCertificate}
            variant="contained"
            color="success"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? 'Sending...' : 'Send to Director'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disapprove Certificate Dialog */}
      <Dialog
        open={disapproveDialogOpen}
        onClose={() => !loading && setDisapproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloseIcon color="error" />
            Request Changes on Certificate
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please provide remarks for requesting changes to this certificate:
          </Typography>
          {cabDetails && (
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2">
                Application No: {cabDetails.applicationNo}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Status: {cabDetails.stage}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks *"
            placeholder="Enter reason for requesting changes..."
            value={disapproveRemarks}
            onChange={(e) => setDisapproveRemarks(e.target.value)}
            disabled={loading}
            error={!disapproveRemarks && error}
            helperText={!disapproveRemarks && error ? 'Remarks are required' : ''}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="password"
            label="Password *"
            placeholder="Enter your password..."
            value={disapprovePassword}
            onChange={(e) => setDisapprovePassword(e.target.value)}
            disabled={loading}
            error={!disapprovePassword && error}
            helperText={!disapprovePassword && error ? 'Password is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDisapproveDialogOpen(false);
              setDisapprovePassword('');
              setDisapproveRemarks('');
              setSelectedApplication(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDisapproveCertificate}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CloseIcon />}
          >
            {loading ? 'Requesting Changes...' : 'Request Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppDashboard;
